import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge as BadgeUI } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, DollarSign, Search, Heart, Star, Briefcase, Clock, Shield, Tag, Facebook, TrendingUp, Badge, WifiOff, RefreshCw, Users, Loader2 } from 'lucide-react';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import { useSavedJobs } from '../hooks/useSavedJobs';
import { usePricing } from '../hooks/usePricing';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { fetchJobsOffline, fetchCategoriesOffline } from '../lib/offlineApi';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import api from '../lib/api';
import { supabase } from '../lib/supabase';
import SEO, { generateWebsiteSchema, generateOrganizationSchema } from '../components/SEO';
import LanguageSelectionModal from '../components/LanguageSelectionModal';
import BannerCarousel from '../components/BannerCarousel';
import PesoBadge from '../components/PesoBadge';
import { rememberReturnTo } from '../lib/returnTo';
import { reportApplyIntent } from '../lib/pixel';

/**
 * Modern Mobile-First Landing Page
 * - Hero with Bohol imagery
 * - Search and filter chips
 * - Trust bar
 * - Listings table
 * - Footer CTA
 */
const Jobs = ({ profile, setProfile }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const isCebuano = currentLang === 'ceb';
  
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filter state from URL params (makes URLs shareable)
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');
  const [locationFilter, setLocationFilter] = useState(searchParams.get('location') || 'all');
  const [postTypeFilter, setPostTypeFilter] = useState(searchParams.get('type') || 'all');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || 'all');
  const [employerFilter, setEmployerFilter] = useState(searchParams.get('employer') || 'all');
  const [categories, setCategories] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobsForCategoryFilter, setJobsForCategoryFilter] = useState([]); // Jobs filtered by postType + location (for category dropdown)
  const [jobsForLocationFilter, setJobsForLocationFilter] = useState([]); // Jobs filtered by postType + category (for location dropdown)
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(10); // For pagination
  const [loadingMore, setLoadingMore] = useState(false); // For infinite scroll loading indicator
  const sentinelRef = useRef(null); // Ref for intersection observer sentinel
  const [appVersion, setAppVersion] = useState('3.1.1');
  const [isFromCache, setIsFromCache] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [applicationCounts, setApplicationCounts] = useState({}); // Admin: application counts per job
  const [landingStats, setLandingStats] = useState(null); // Landing page stats
  const [hasActiveBanners, setHasActiveBanners] = useState(false); // Employer banner carousel: true once >=1 active banner loads

  // Quick filters
  const [quickFilters, setQuickFilters] = useState({
    urgent_hiring: searchParams.get('urgent') === '1',
    no_experience_needed: searchParams.get('noexp') === '1',
    with_housing: searchParams.get('housing') === '1',
    peso: searchParams.get('peso') === '1'
  });

  // Sync filters to URL (shareable links)
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (categoryFilter !== 'all') params.set('category', categoryFilter);
    if (locationFilter !== 'all') params.set('location', locationFilter);
    if (postTypeFilter !== 'all') params.set('type', postTypeFilter);
    if (employerFilter !== 'all') params.set('employer', employerFilter);
    if (quickFilters.urgent_hiring) params.set('urgent', '1');
    if (quickFilters.no_experience_needed) params.set('noexp', '1');
    if (quickFilters.with_housing) params.set('housing', '1');
    if (quickFilters.peso) params.set('peso', '1');
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, categoryFilter, locationFilter, postTypeFilter, employerFilter, quickFilters, setSearchParams]);
  
  // Network status for offline support
  const { isOnline, justCameOnline } = useNetworkStatus();
  
  // Check if user is admin
  const isAdmin = profile?.role === 'admin';
  
  // Helper to get localized field
  const getLocalizedField = (job, field) => {
    if (isCebuano && job[`${field}_ceb`]) {
      return job[`${field}_ceb`];
    }
    return job[field];
  };

  // Save job functionality - only for logged in workers
  const isWorker = profile?.role === 'job_seeker' || profile?.role === 'contractor';
  const { savedJobIds, toggleSaveJob } = useSavedJobs(profile);

  // Fetch categories, version, and landing stats on mount
  useEffect(() => {
    const fetchCategoriesAndVersion = async () => {
      try {
        const [categoriesResult, versionResponse, statsResponse] = await Promise.all([
          fetchCategoriesOffline(),
          api.get('/api/settings/version').then(r => r.data).catch(() => ({})),
          api.get('/api/stats/landing').then(r => r.data).catch(() => ({}))
        ]);
        setCategories(categoriesResult.categories || []);
        if (versionResponse.app_version) {
          setAppVersion(versionResponse.app_version);
        }
        if (statsResponse.open_positions !== undefined) {
          setLandingStats(statsResponse);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchCategoriesAndVersion();
  }, []);

  // Debounce search term for server-side search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch application counts for admins
  useEffect(() => {
    const fetchApplicationCounts = async () => {
      if (!isAdmin) return;
      
      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) return;
        
        const response = await api.get('/api/admin/jobs/application-counts', {
          headers: { Authorization: `Bearer ${session.session.access_token}` }
        });
        setApplicationCounts(response.data.counts || {});
      } catch (error) {
        console.error('Error fetching application counts:', error);
      }
    };
    
    fetchApplicationCounts();
  }, [isAdmin]);

  // Fetch jobs (public endpoint) - all filters applied for display with offline support
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const result = await fetchJobsOffline({
          location: locationFilter,
          post_type: postTypeFilter,
          category_id: categoryFilter,
          employer_id: employerFilter,
          search: debouncedSearch || undefined
        });
        setJobs(result.jobs || []);
        setIsFromCache(result.fromCache || false);
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [locationFilter, postTypeFilter, categoryFilter, employerFilter, debouncedSearch]);

  // Refresh data when coming back online
  useEffect(() => {
    if (justCameOnline) {
      console.log('[Jobs] Back online, refreshing data...');
      setIsRefreshing(true);
      fetchJobsOffline({
        location: locationFilter,
        post_type: postTypeFilter,
        category_id: categoryFilter,
        employer_id: employerFilter,
        search: debouncedSearch || undefined
      }).then((result) => {
        setJobs(result.jobs || []);
        setIsFromCache(result.fromCache || false);
      }).finally(() => {
        setIsRefreshing(false);
      });
    }
  }, [justCameOnline, locationFilter, postTypeFilter, categoryFilter, employerFilter, debouncedSearch]);

  // Manual refresh handler
  const handleRefresh = async () => {
    if (!isOnline) return;
    setIsRefreshing(true);
    try {
      const result = await fetchJobsOffline({
        location: locationFilter,
        post_type: postTypeFilter,
        category_id: categoryFilter,
        employer_id: employerFilter,
        search: debouncedSearch || undefined
      });
      setJobs(result.jobs || []);
      setIsFromCache(result.fromCache || false);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Fetch jobs for category dropdown (filtered by postType + location only) with offline support
  useEffect(() => {
    const fetchJobsForCategoryFilter = async () => {
      try {
        const result = await fetchJobsOffline({
          post_type: postTypeFilter,
          location: locationFilter,
          search: debouncedSearch || undefined
        });
        setJobsForCategoryFilter(result.jobs || []);
      } catch (error) {
        console.error('Error fetching jobs for category filter:', error);
      }
    };
    fetchJobsForCategoryFilter();
  }, [postTypeFilter, locationFilter, debouncedSearch]); // Exclude categoryFilter

  // Fetch jobs for location dropdown (filtered by postType + category only) with offline support
  useEffect(() => {
    const fetchJobsForLocationFilter = async () => {
      try {
        const result = await fetchJobsOffline({
          post_type: postTypeFilter,
          category_id: categoryFilter,
          search: debouncedSearch || undefined
        });
        setJobsForLocationFilter(result.jobs || []);
      } catch (error) {
        console.error('Error fetching jobs for location filter:', error);
      }
    };
    fetchJobsForLocationFilter();
  }, [postTypeFilter, categoryFilter, debouncedSearch]); // Exclude locationFilter

  // Extract unique locations from jobs filtered by postType + category (but NOT location)
  const uniqueLocations = useMemo(() => {
    const locations = [...new Set(
      jobsForLocationFilter.flatMap(job =>
        job.locations || (job.location ? [job.location] : [])
      ).filter(Boolean)
    )];
    return locations.sort();
  }, [jobsForLocationFilter]);

  // Get available categories from jobs filtered by postType + location (but NOT category)
  const availableCategories = useMemo(() => {
    return categories.filter(cat => jobsForCategoryFilter.some(job => job.category_id === cat.id));
  }, [categories, jobsForCategoryFilter]);

  // Get unique employers from unfiltered jobs (uses jobsForCategoryFilter which excludes category/employer filters)
  const availableEmployers = useMemo(() => {
    const empMap = {};
    jobsForCategoryFilter.forEach(job => {
      const emp = job.employer;
      if (emp?.id && (emp.business_name || emp.name)) {
        if (!empMap[emp.id]) {
          empMap[emp.id] = { id: emp.id, name: emp.business_name || emp.name };
        }
      }
    });
    return Object.values(empMap).sort((a, b) => a.name.localeCompare(b.name));
  }, [jobsForCategoryFilter]);

  const filteredJobs = jobs
    .filter(job => {
      const q = searchTerm.trim().toLowerCase();
      if (!q) return true;
      const matchesSearch =
        job.title?.toLowerCase().includes(q) ||
        job.description?.toLowerCase().includes(q) ||
        job.title_ceb?.toLowerCase().includes(q) ||
        job.description_ceb?.toLowerCase().includes(q) ||
        job.employer?.business_name?.toLowerCase().includes(q) ||
        job.employer?.name?.toLowerCase().includes(q) ||
        (job.requirements && job.requirements.some(r => (r || '').toLowerCase().includes(q))) ||
        (job.requirements_ceb && job.requirements_ceb.some(r => (r || '').toLowerCase().includes(q)));
      return matchesSearch;
    })
    .filter(job => {
      // Quick filters
      if (quickFilters.urgent_hiring && !job.urgent_hiring) return false;
      if (quickFilters.no_experience_needed && !job.no_experience_needed) return false;
      if (quickFilters.with_housing && !job.with_housing) return false;
      if (quickFilters.peso && !job.employer?.peso_office) return false;
      return true;
    })
    .sort((a, b) => {
      // Featured first, then Boosted (last 24h), then by date desc
      const aFeatured = a.is_featured && new Date(a.featured_until) > new Date();
      const bFeatured = b.is_featured && new Date(b.featured_until) > new Date();
      if (aFeatured && !bFeatured) return -1;
      if (!aFeatured && bFeatured) return 1;
      const aBoosted = a.boosted_at && new Date() - new Date(a.boosted_at) < 24 * 60 * 60 * 1000;
      const bBoosted = b.boosted_at && new Date() - new Date(b.boosted_at) < 24 * 60 * 60 * 1000;
      if (aBoosted && !bBoosted) return -1;
      if (!aBoosted && bBoosted) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(10);
  }, [searchTerm, locationFilter, postTypeFilter, categoryFilter, employerFilter, quickFilters]);

  // Check if there are more items to load
  const hasMore = filteredJobs.length > visibleCount;

  // Load more items function
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    // Small delay to show loading indicator
    setTimeout(() => {
      setVisibleCount(prev => prev + 10);
      setLoadingMore(false);
    }, 300);
  }, [loadingMore, hasMore]);

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loading && !loadingMore) {
          loadMore();
        }
      },
      {
        root: null, // viewport
        rootMargin: '100px', // trigger 100px before reaching the sentinel
        threshold: 0.1
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore, loadMore]);

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const posted = new Date(dateString);
    const diffMs = now - posted;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  // Helper to get job/gig URL (use slug if available, otherwise ID)
  // Routes to /gigs/ for gigs, /jobs/ for jobs
  const getJobUrl = (job) => {
    const basePath = job.post_type === 'gig' ? '/gigs' : '/jobs';
    return job.slug ? `${basePath}/${job.slug}` : `${basePath}/${job.id}`;
  };

  const handleApplyClick = (e, job) => {
    e.stopPropagation();
    const jobUrl = getJobUrl(job);
    if (!profile) {
      reportApplyIntent({ jobId: job.id, postType: job.post_type });
      rememberReturnTo(jobUrl);
      navigate('/auth');
    } else {
      navigate(jobUrl);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setLocationFilter('all');
    setPostTypeFilter('all');
    setCategoryFilter('all');
    setEmployerFilter('all');
    setQuickFilters({
      urgent_hiring: false,
      no_experience_needed: false,
      with_housing: false,
      peso: false
    });
  };

  // Mobile card renderer
  const renderJobCard = (job) => {
    const isFeatured = job.is_featured && new Date(job.featured_until) > new Date();
    const isBoosted = job.boosted_at && new Date() - new Date(job.boosted_at) < 24 * 60 * 60 * 1000;
    const isSaved = savedJobIds.includes(job.id);
    const isGig = job.post_type === 'gig';

    return (
      <div 
        key={job.id} 
        className="relative w-full max-w-full overflow-hidden" 
        data-testid="job-card" 
        onClick={() => navigate(getJobUrl(job))}
      >
        <Card 
          className={`rounded-xl border p-3 sm:p-4 hover:shadow-2xl transition-all w-full max-w-full box-border ${
            isFeatured 
              ? 'bg-gradient-to-br from-yellow-50 to-amber-100 border-yellow-300 shadow-lg' 
              : isBoosted 
              ? 'bg-gradient-to-br from-purple-50 to-pink-100 border-purple-300 shadow-lg animate-pulse-slow' 
              : 'bg-card hover:shadow-xl'
          }`}
          style={
            isFeatured 
              ? { boxShadow: '0 4px 25px rgba(245, 158, 11, 0.4)' } 
              : isBoosted 
              ? { boxShadow: '0 4px 25px rgba(168, 85, 247, 0.4)' } 
              : {}
          }
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-base md:text-lg break-words">{getLocalizedField(job, 'title')}</h3>
                {isGig ? (
                  <span className="text-[11px] border px-2 py-0.5 rounded-full border-teal-400 text-teal-700 bg-teal-50 flex-shrink-0" data-testid="gig-chip">
                    <Clock className="inline-block mr-1" size={12} />Gig
                  </span>
                ) : (
                  <span className="text-[11px] border px-2 py-0.5 rounded-full border-emerald-400 text-emerald-700 bg-emerald-50 flex-shrink-0" data-testid="job-chip">Job</span>
                )}
                {isFeatured && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-bold shadow-md flex items-center gap-1 flex-shrink-0" data-testid="featured-chip">
                    <Star size={10} className="fill-white" />
                    Featured
                  </span>
                )}
                {isBoosted && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold shadow-md flex items-center gap-1 flex-shrink-0" data-testid="boosted-chip">
                    <TrendingUp size={10} />
                    Boosted
                  </span>
                )}
                {/* Admin: Show application/bid count */}
                {isAdmin && (
                  <span 
                    className={`text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0 ${
                      applicationCounts[job.id] > 0 
                        ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                        : 'bg-gray-100 text-gray-500 border border-gray-300'
                    }`}
                    title={`${applicationCounts[job.id] || 0} ${isGig ? 'bids' : 'applications'}`}
                  >
                    <Users size={10} />
                    {applicationCounts[job.id] || 0}
                  </span>
                )}
              </div>

              {/* Quick Filter Badges */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {job.urgent_hiring && (
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium dark:bg-red-950 dark:text-red-300 dark:border-red-800">
                    🔴 Urgent Hiring
                  </span>
                )}
                {job.no_experience_needed && (
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium dark:bg-green-950 dark:text-green-300 dark:border-green-800">
                    ⭐ No Experience
                  </span>
                )}
                {job.with_housing && (
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                    🏠 Housing
                  </span>
                )}
                {job.positions_available > 1 && (
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-medium dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800">
                    <Users size={12} /> Hiring {job.positions_available}
                  </span>
                )}
                {job.source === 'community_board' && (
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 font-medium dark:bg-cyan-950 dark:text-cyan-300 dark:border-cyan-800">
                    Community Board
                  </span>
                )}
                {job.employer?.peso_office && (
                  <PesoBadge officeName={job.employer.peso_office_name} color={job.employer.peso_office_color} className="px-2.5 py-1" />
                )}
              </div>
            </div>
            <button
              aria-label={isSaved ? 'Unsave job' : 'Save job'}
              onClick={(e) => {
                e.stopPropagation();
                if (!profile) {
                  navigate('/sign-up');
                } else {
                  toggleSaveJob(job.id);
                }
              }}
              className="h-9 w-9 inline-flex items-center justify-center rounded-full border hover:bg-muted transition-colors"
              data-testid="save-job-button"
              title={isSaved ? 'Unsave' : 'Save'}
            >
              <Heart size={16} className={isSaved ? 'fill-red-500 text-red-500' : 'text-gray-500'} />
            </button>
          </div>
          <div className="mt-3 flex items-center flex-wrap gap-3 text-sm">
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <MapPin size={14} className="text-primary" />{(() => { const locs = (job.locations || [job.location]).filter(Boolean); return locs.length <= 3 ? locs.join(', ') : `${locs.slice(0, 3).join(', ')} +${locs.length - 3} more`; })()}
            </span>
            <span className="text-xs text-muted-foreground">{getTimeAgo(job.created_at)}</span>
            {(job.employer?.business_name || job.employer?.name) && (
              job.source === 'community_board' ? (
                <span className="inline-flex items-center gap-2 text-foreground/90 text-sm">
                  <Avatar className="h-6 w-6"><AvatarFallback className="text-[10px] bg-cyan-100 text-cyan-700">CB</AvatarFallback></Avatar>
                  Community Board
                </span>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/employers/${job.employer.id}`);
                  }}
                  className="inline-flex items-center gap-2 hover:text-primary transition-colors group"
                >
                  <Avatar className="h-6 w-6"><AvatarFallback className="text-[10px] bg-secondary">{(job.employer?.business_name?.[0] || job.employer?.name?.[0] || 'A')}</AvatarFallback></Avatar>
                  <span className="text-foreground/90 text-sm group-hover:underline">{job.employer.business_name || job.employer.name}</span>
                  {job.employer?.verified && (
                    <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />
                  )}
                  {job.employer?.rating > 0 && (
                    <span className="text-xs text-yellow-700 inline-flex items-center gap-1"><Star size={12} />{job.employer.rating.toFixed(1)}</span>
                  )}
                </button>
              )
            )}
            <span className="inline-flex items-center gap-1 font-medium text-primary">
              { (job.salary_min || job.salary_max)
                ? <><span className="font-bold">₱</span>{`${(job.salary_min || 0).toLocaleString()}${job.salary_max ? ` - ₱${job.salary_max.toLocaleString()}` : ''}${job.salary_period ? `/${job.salary_period === 'daily' ? 'day' : 'month'}` : ''}`}</>
                : <span className="text-muted-foreground font-normal">Salary not disclosed</span> }
            </span>
          </div>
          <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground" aria-label="Posted">{getTimeAgo(job.created_at)}</span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button size="sm" className="rounded-lg bg-[#0052CC] hover:bg-[#0047B3] text-white whitespace-nowrap" onClick={(e) => handleApplyClick(e, job)} data-testid="apply-from-card-button">{t('jobs.apply')}</Button>
              <Button size="sm" variant="outline" className="rounded-lg border-gray-300 whitespace-nowrap" onClick={(e) => { e.stopPropagation(); navigate(getJobUrl(job)); }} data-testid="view-details-link">{t('jobs.view')}</Button>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  const renderJobRow = (job) => {
    const isFeatured = job.is_featured && new Date(job.featured_until) > new Date();
    const isBoosted = job.boosted_at && (new Date() - new Date(job.boosted_at)) < 24 * 60 * 60 * 1000;
    const isSaved = savedJobIds.includes(job.id);
    const isGig = job.post_type === 'gig';

    return (
      <tr
        key={job.id}
        className={`cursor-pointer transition-all border-b ${
          isFeatured 
            ? 'bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 hover:from-yellow-100 hover:via-amber-100 hover:to-yellow-100 border-yellow-300 border-l-4 shadow-md' 
            : isBoosted 
            ? 'bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 hover:from-purple-100 hover:via-pink-100 hover:to-purple-100 border-purple-300 border-l-4 shadow-md animate-pulse-slow' 
            : 'hover:bg-muted/50'
        }`}
        onClick={() => navigate(getJobUrl(job))}
        data-testid="job-row"
        style={
          isFeatured 
            ? { boxShadow: '0 0 20px rgba(245, 158, 11, 0.3)' } 
            : isBoosted 
            ? { boxShadow: '0 0 20px rgba(168, 85, 247, 0.3)' } 
            : {}
        }
      >
        {/* Status column (chips) */}
        <td className="py-4 px-2 md:px-4 align-top">
          <div className="flex flex-col gap-1">
            {isFeatured && (
              <BadgeUI className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-bold text-xs flex items-center gap-1 shadow-lg" data-testid="featured-chip">
                <Star size={12} className="fill-white" />
                Featured
              </BadgeUI>
            )}
            {isBoosted && (
              <BadgeUI className="bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-xs flex items-center gap-1 shadow-lg" data-testid="boosted-chip">
                <TrendingUp size={12} />
                Boosted
              </BadgeUI>
            )}
            {/* Admin: Show application/bid count */}
            {isAdmin && (
              <BadgeUI 
                className={`text-xs flex items-center gap-1 ${
                  applicationCounts[job.id] > 0 
                    ? 'bg-blue-100 text-blue-700 border-blue-300' 
                    : 'bg-gray-100 text-gray-500 border-gray-300'
                }`}
                variant="outline"
                title={`${applicationCounts[job.id] || 0} ${isGig ? 'bids' : 'applications'}`}
              >
                <Users size={10} />
                {applicationCounts[job.id] || 0}
              </BadgeUI>
            )}

            {/* Quick Filter Badges in Table */}
            <div className="flex flex-wrap gap-1 mt-1">
              {job.urgent_hiring && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">
                  🔴 Urgent
                </span>
              )}
              {job.no_experience_needed && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium">
                  ⭐ No Exp
                </span>
              )}
              {job.with_housing && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                  🏠 Housing
                </span>
              )}
              {job.positions_available > 1 && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-medium flex items-center gap-1">
                  <Users size={10} /> Hiring {job.positions_available}
                </span>
              )}
              {job.source === 'community_board' && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 font-medium">
                  Community Board
                </span>
              )}
              {job.employer?.peso_office && (
                <PesoBadge officeName={job.employer.peso_office_name} color={job.employer.peso_office_color} />
              )}
            </div>
          </div>
        </td>

        {/* Job Title */}
        <td className="py-4 px-2 md:px-4">
          <div className="flex items-start gap-2">
            <div className="font-semibold text-sm md:text-base text-foreground hover:text-primary transition-colors break-words whitespace-normal">
              {getLocalizedField(job, 'title')}
            </div>
            {isGig && (
              <BadgeUI variant="outline" className="text-xs flex-shrink-0"> <Clock size={10} className="mr-1" /> Gig</BadgeUI>
            )}
          </div>
          {/* Show location on mobile (hidden column) */}
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 sm:hidden">
            <MapPin size={12} className="text-primary" />
            {(() => { const locs = (job.locations || [job.location]).filter(Boolean); return locs.length <= 3 ? locs.join(', ') : `${locs.slice(0, 3).join(', ')} +${locs.length - 3} more`; })()}
          </div>
        </td>

        {/* Location */}
        <td className="py-4 px-2 md:px-4 hidden sm:table-cell">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin size={14} className="text-primary" />
            {(() => { const locs = (job.locations || [job.location]).filter(Boolean); return locs.length <= 3 ? locs.join(', ') : `${locs.slice(0, 3).join(', ')} +${locs.length - 3} more`; })()}
          </div>
        </td>

        {/* Salary/Budget */}
        <td className="py-4 px-2 md:px-4 hidden md:table-cell">
          {(job.salary_min || job.salary_max) ? (
            <div className="flex items-center gap-1 text-sm font-semibold text-primary whitespace-nowrap">
              <span className="font-bold">₱</span>
              {job.salary_min?.toLocaleString() || '0'} - ₱{job.salary_max?.toLocaleString() || '0'}
              {job.salary_period && <span className="text-xs text-muted-foreground">/{job.salary_period === 'daily' ? 'day' : 'month'}</span>}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">Salary not disclosed</span>
          )}
        </td>

        {/* Employer */}
        <td className="py-4 px-2 md:px-4 hidden lg:table-cell">
          <div className="flex items-center gap-2">
            <Avatar className="w-8 h-8">
              <AvatarFallback className={`text-xs ${job.source === 'community_board' ? 'bg-cyan-100 text-cyan-700' : 'bg-secondary'}`}>
                {job.source === 'community_board' ? 'CB' : (job.employer?.business_name?.[0] || job.employer?.name?.[0] || 'A')}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="text-sm font-medium flex items-center gap-1">
                {job.source === 'community_board' ? 'Community Board' : (job.employer?.business_name || job.employer?.name || 'Anonymous')}
                {job.source !== 'community_board' && job.employer?.verified && (
                  <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />
                )}
              </div>
              {job.source !== 'community_board' && job.employer?.rating > 0 && (
                <div className="text-xs text-yellow-600 flex items-center gap-1">
                  <Star size={10} className="fill-yellow-500" />
                  {job.employer.rating.toFixed(1)}
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Posted Date */}
        <td className="py-4 px-2 md:px-4 hidden md:table-cell">
          <div className="text-xs text-muted-foreground whitespace-nowrap">
            {getTimeAgo(job.created_at)}
          </div>
        </td>

        {/* Actions */}
        <td className="py-4 pl-1 pr-2 md:px-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1 justify-end">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-transparent flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                if (!profile) {
                  navigate('/sign-up');
                } else {
                  toggleSaveJob(job.id);
                }
              }}
              data-testid="save-job-btn"
              title={isSaved ? 'Unsave' : 'Save'}
            >
              <Heart
                size={16}
                className={isSaved ? 'fill-red-500 text-red-500' : 'text-gray-400 hover:text-red-400'}
                strokeWidth={2}
              />
            </Button>
            <Button
              size="sm"
              className="rounded-lg bg-[#0052CC] hover:bg-[#0047B3] text-white"
              onClick={(e) => handleApplyClick(e, job)}
              data-testid="apply-btn"
            >
              Apply
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  // Generate combined schema for homepage
  const homepageSchema = [generateWebsiteSchema(), generateOrganizationSchema()];

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0 bg-gray-50">
      {/* SEO: Homepage meta tags with WebSite and Organization schema */}
      <SEO
        title="Jobs & Services in Bohol"
        description="Find jobs and services in Bohol, Philippines. Browse verified job listings from local employers in Tagbilaran, Panglao, Carmen, and all of Bohol. Free for job seekers!"
        url="/jobs"
        schema={homepageSchema}
        keywords={[
          'jobs in Bohol',
          'trabaho sa Bohol',
          'Bohol jobs',
          'hiring Bohol',
          'jobs Tagbilaran',
          'jobs Panglao',
          'Bohol employment',
          'work in Bohol',
          'Boholano jobs',
          'Philippines jobs'
        ]}
      />
      
      <TopBar profile={profile} setProfile={setProfile} />

      {/* Language selection popup - only on this landing page */}
      <LanguageSelectionModal />

      {/* Employer banner carousel (paid placements) — shown to everyone, no profile gate.
          Falls back to the Trust & Value Proposition Bar below while loading, on fetch
          error, or when there are 0 active banners, so the hero slot is never blank.
          v2: constrained to the same content width as the Latest Jobs table (spec §6). */}
      <div className={hasActiveBanners ? 'max-w-7xl mx-auto px-3 sm:px-4 pt-4' : ''}>
        <BannerCarousel onBannersChange={setHasActiveBanners} />
      </div>
      {!hasActiveBanners && (
      <div className="relative bg-gradient-to-r from-primary/5 via-white to-primary/5 border-y border-primary/20">
        <div className="max-w-7xl mx-auto px-3 py-4 md:py-8">
          {/* Mobile: 3 cards in row */}
          <div className="md:hidden grid grid-cols-3 gap-2">
            <div className="flex flex-col items-center text-center space-y-1.5 p-2 rounded-xl bg-white/70 border border-teal-200 shadow-sm">
              <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-md">
                <Shield size={18} className="text-white" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-[10px] font-bold text-foreground leading-tight">{t('landing.trueBoholano')}</p>
                <p className="text-[9px] text-teal-600 font-semibold">{t('landing.idVerified')}</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center space-y-1.5 p-2 rounded-xl bg-white/70 border border-accent/20 shadow-sm">
              <div className="w-9 h-9 bg-gradient-to-br from-accent to-accent/80 rounded-full flex items-center justify-center shadow-md">
                <Star size={18} className="text-white fill-white" strokeWidth={2} />
              </div>
              <div>
                <p className="text-[10px] font-bold text-foreground leading-tight">{t('landing.twoWay')}</p>
                <p className="text-[9px] text-accent font-semibold">{t('landing.reviews')}</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center space-y-1.5 p-2 rounded-xl bg-white/70 border border-green-600/20 shadow-sm cursor-pointer active:scale-95 transition-transform" onClick={() => navigate('/sign-up')}>
              <div className="w-9 h-9 bg-gradient-to-br from-green-600 to-green-500 rounded-full flex items-center justify-center shadow-md">
                <Tag size={18} className="text-white" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-[10px] font-bold text-foreground leading-tight">{t('landing.freeFor')}</p>
                <p className="text-[9px] text-green-600 font-semibold">{t('landing.workers')}</p>
              </div>
            </div>
          </div>
          {/* Desktop: Full grid */}
          <div className="hidden md:grid grid-cols-3 gap-6 lg:gap-8">
            <div className="group flex flex-col items-center text-center space-y-3 p-4 rounded-2xl bg-white/60 backdrop-blur-sm shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-teal-200">
              <div className="relative">
                <div className="absolute inset-0 bg-teal-500/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                  <Shield size={32} className="text-white" strokeWidth={2.5} />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground mb-1">{t('landing.trueBoholanoBadge')}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t('landing.idVerification')}</p>
                <p className="text-xs text-teal-600 font-semibold mt-1">{t('landing.badgeExplanation')}</p>
              </div>
            </div>
            <div className="group flex flex-col items-center text-center space-y-3 p-4 rounded-2xl bg-white/60 backdrop-blur-sm shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-accent/10">
              <div className="relative">
                <div className="absolute inset-0 bg-accent/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-accent to-accent/80 rounded-full flex items-center justify-center shadow-lg">
                  <Star size={32} className="text-white fill-white" strokeWidth={2} />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground mb-1">{t('landing.twoWayReviews')}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t('landing.rateEmployersWorkers')}</p>
                <p className="text-xs text-accent font-semibold mt-1">{t('landing.honestFeedback')}</p>
              </div>
            </div>
            <div className="group flex flex-col items-center text-center space-y-3 p-4 rounded-2xl bg-white/60 backdrop-blur-sm shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-green-600/10 cursor-pointer" onClick={() => navigate('/sign-up')}>
              <div className="relative">
                <div className="absolute inset-0 bg-green-600/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-green-600 to-green-500 rounded-full flex items-center justify-center shadow-lg">
                  <Tag size={32} className="text-white" strokeWidth={2.5} />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground mb-1">{t('landing.freeForWorkers')}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t('landing.jobSeekersNeverPay')}</p>
                <p className="text-xs text-green-600 font-semibold mt-1">{t('landing.signUpApplyFree')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Search Bar - Full Width */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder={landingStats ? `Search ${landingStats.open_positions}+ open positions in Bohol...` : t('landing.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 text-base rounded-xl border-gray-300 focus:border-[#0052CC] focus:ring-[#0052CC]"
              style={{ fontFamily: 'Inter, sans-serif' }}
              data-testid="main-search-input"
            />
          </div>
        </div>
      </div>

      {/* Filter Chips - Horizontal - More compact */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={postTypeFilter} onValueChange={setPostTypeFilter}>
              <SelectTrigger 
                className={`rounded-full px-3 py-1.5 text-sm border h-auto w-auto min-w-[120px] ${postTypeFilter !== 'all' ? 'bg-[#0052CC] text-white border-[#0052CC]' : 'bg-white border-gray-300'}`}
                data-testid="type-chip"
              >
                <SelectValue placeholder={t('jobs.allTypes')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('jobs.allJobs')}</SelectItem>
                <SelectItem value="job">{t('jobs.fullTime')}</SelectItem>
                <SelectItem value="gig">{t('gigs.title')}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger 
                className={`rounded-full px-3 py-1.5 text-sm border h-auto w-auto min-w-[140px] ${categoryFilter !== 'all' ? 'bg-[#0052CC] text-white border-[#0052CC]' : 'bg-white border-gray-300'}`}
                data-testid="category-chip"
              >
                <SelectValue placeholder={t('jobs.allCategories')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('jobs.allCategories')}</SelectItem>
                {availableCategories.map(category => (
                  <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger
                className={`rounded-full px-3 py-1.5 text-sm border h-auto w-auto min-w-[140px] ${locationFilter !== 'all' ? 'bg-[#0052CC] text-white border-[#0052CC]' : 'bg-white border-gray-300'}`}
                data-testid="location-chip"
              >
                <SelectValue placeholder={t('jobs.allLocations')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('jobs.allLocations')}</SelectItem>
                {uniqueLocations.map(location => (
                  <SelectItem key={location} value={location}>{location}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {availableEmployers.length > 1 && (
              <Select value={employerFilter} onValueChange={setEmployerFilter}>
                <SelectTrigger
                  className={`rounded-full px-3 py-1.5 text-sm border h-auto w-auto min-w-[140px] ${employerFilter !== 'all' ? 'bg-[#0052CC] text-white border-[#0052CC]' : 'bg-white border-gray-300'}`}
                  data-testid="employer-chip"
                >
                  <SelectValue placeholder="All Employers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employers</SelectItem>
                  {availableEmployers.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Quick Filter Chips */}
            <button
              onClick={() => setQuickFilters({ ...quickFilters, urgent_hiring: !quickFilters.urgent_hiring })}
              className={`rounded-full px-3 py-1.5 text-sm border h-auto whitespace-nowrap transition-colors ${
                quickFilters.urgent_hiring
                  ? 'bg-red-500 text-white border-red-500'
                  : 'bg-white border-gray-300 hover:border-red-300'
              }`}
              data-testid="urgent-chip"
            >
              🔴 Urgent Hiring
            </button>

            <button
              onClick={() => setQuickFilters({ ...quickFilters, no_experience_needed: !quickFilters.no_experience_needed })}
              className={`rounded-full px-3 py-1.5 text-sm border h-auto whitespace-nowrap transition-colors ${
                quickFilters.no_experience_needed
                  ? 'bg-green-500 text-white border-green-500'
                  : 'bg-white border-gray-300 hover:border-green-300'
              }`}
              data-testid="no-experience-chip"
            >
              ⭐ No Experience Needed
            </button>

            <button
              onClick={() => setQuickFilters({ ...quickFilters, with_housing: !quickFilters.with_housing })}
              className={`rounded-full px-3 py-1.5 text-sm border h-auto whitespace-nowrap transition-colors ${
                quickFilters.with_housing
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white border-gray-300 hover:border-blue-300'
              }`}
              data-testid="housing-chip"
            >
              🏠 With Housing
            </button>

            <button
              onClick={() => setQuickFilters({ ...quickFilters, peso: !quickFilters.peso })}
              className={`rounded-full px-3 py-1.5 text-sm border h-auto whitespace-nowrap transition-colors ${
                quickFilters.peso
                  ? 'bg-[#1d4ed8] text-white border-[#1d4ed8]'
                  : 'bg-white border-gray-300 hover:border-blue-400'
              }`}
              data-testid="peso-chip"
            >
              🏛️ {t('peso.filterChip')}
            </button>

            {(postTypeFilter !== 'all' || categoryFilter !== 'all' || locationFilter !== 'all' || employerFilter !== 'all' ||
              quickFilters.urgent_hiring || quickFilters.no_experience_needed || quickFilters.with_housing || quickFilters.peso) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-sm text-gray-600"
                data-testid="clear-filters-btn"
              >
                Clear all
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Listings Section */}
      <div id="listings" className="bg-white px-3 sm:px-4 py-8 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              {t('landing.latestJobs')}
            </h2>
            {/* Cached data indicator */}
            {isFromCache && !loading && (
              <div className="flex items-center gap-1.5 text-amber-600 bg-amber-50 px-2 py-1 rounded-full text-xs font-medium">
                <WifiOff size={12} />
                <span>{t('network.cachedData', 'Cached')}</span>
                {isOnline && (
                  <button 
                    onClick={handleRefresh}
                    className="ml-1 hover:text-amber-800"
                    disabled={isRefreshing}
                    title="Refresh"
                  >
                    <RefreshCw size={12} className={isRefreshing ? 'animate-spin' : ''} />
                  </button>
                )}
              </div>
            )}
          </div>
          <Button 
            onClick={() => navigate(profile ? '/post-job' : '/sign-up')}
            className="bg-[#228B22] hover:bg-[#1e7a1e] text-white rounded-xl"
            data-testid="post-job-cta"
          >
            {t('post.postJob')}
          </Button>
        </div>

        {/* Mobile & Tablet Card List */}
        {!loading && filteredJobs.length > 0 && (
          <>
            <div className="lg:hidden grid gap-3 w-full" data-testid="jobs-list-mobile">
              {filteredJobs.slice(0, visibleCount).map(renderJobCard)}
            </div>
            {/* Status for Mobile */}
            <div className="lg:hidden flex flex-col items-center gap-2 mt-6">
              <p className="text-sm text-gray-600">
                Showing {Math.min(filteredJobs.length, visibleCount)} of {filteredJobs.length} {filteredJobs.length === 1 ? 'opening' : 'openings'}
              </p>
              {!hasMore && filteredJobs.length > 10 && (
                <p className="text-xs text-muted-foreground">{t('jobs.allJobsLoaded', "You've seen all available jobs")}</p>
              )}
            </div>
          </>
        )}

        {/* Desktop table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <Card className="p-10 text-center bg-card border rounded-xl relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none bg-[url('https://images.unsplash.com/photo-1615185403293-a8fb0482d24e?crop=entropy&cs=srgb&fm=jpg&q=85')] bg-cover opacity-[0.06]" aria-hidden="true" />
            <div className="relative">
              <h3 className="font-heading text-xl font-semibold mb-2">{t('jobs.noJobsMatch')}</h3>
              <p className="text-muted-foreground mb-4">{t('jobs.tryClearing')}</p>
              <div className="flex items-center gap-3 justify-center flex-wrap">
                <Button variant="outline" onClick={clearFilters} className="rounded-xl" data-testid="clear-filter-button">Clear filters</Button>
                {!profile && (
                  <Button onClick={() => navigate('/sign-up')} className="rounded-xl" data-testid="signup-cta-empty">Sign up to post</Button>
                )}
              </div>
            </div>
          </Card>
        ) : (
          <Card className="hidden lg:block shadow-sm rounded-xl border border-gray-200">
            <div>
              <table className="w-full" data-testid="jobs-table" style={{ fontFamily: 'Inter, sans-serif' }}>
                <thead className="border-b bg-gray-50">
                  <tr>
                    <th className="py-4 px-4 text-left font-semibold text-sm text-gray-700">{t('common.status') || 'Status'}</th>
                    <th className="py-4 px-4 text-left font-semibold text-sm text-gray-700">{t('post.title')}</th>
                    <th className="py-4 px-4 text-left font-semibold text-sm text-gray-700">{t('jobs.location')}</th>
                    <th className="py-4 px-4 text-left font-semibold text-sm text-gray-700">{t('jobs.salary')}</th>
                    <th className="py-4 px-4 text-left font-semibold text-sm text-gray-700">{t('profile.employer')}</th>
                    <th className="py-4 px-4 text-left font-semibold text-sm text-gray-700">{t('jobs.postedOn')}</th>
                    <th className="py-4 px-4 text-right font-semibold text-sm text-gray-700">{t('common.action') || 'Action'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredJobs.slice(0, visibleCount).map(renderJobRow)}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {Math.min(filteredJobs.length, visibleCount)} of {filteredJobs.length} {filteredJobs.length === 1 ? 'opening' : 'openings'}
                {!hasMore && filteredJobs.length > 10 && (
                  <span className="ml-2 text-muted-foreground">- {t('jobs.allJobsLoaded', "You've seen all available jobs")}</span>
                )}
              </p>
              {hasMore && loadingMore && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">{t('common.loading', 'Loading...')}</span>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Infinite Scroll Sentinel & Loading Indicator (for both mobile and desktop) */}
        {!loading && filteredJobs.length > 0 && (
          <div className="flex flex-col items-center gap-2 mt-4">
            {hasMore && (
              <>
                {/* Sentinel element for intersection observer */}
                <div ref={sentinelRef} className="w-full h-4" aria-hidden="true" />
                {loadingMore && (
                  <div className="flex items-center gap-2 text-muted-foreground py-4">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">{t('common.loadingMore', 'Loading more jobs...')}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Inline CTA for non-logged users */}
        {!loading && filteredJobs.length > 0 && !profile && (
          <div className="mt-8 rounded-xl bg-gradient-to-r from-[#0052CC] to-[#0047B3] p-6 text-center">
            <p className="text-white text-lg font-medium mb-4">{t('jobs.createAccountCTA')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button onClick={() => navigate('/sign-up')} className="bg-white text-[#0052CC] hover:bg-gray-100 rounded-xl" data-testid="signup-inline-cta">{t('jobs.createFreeAccount')}</Button>
              <Button variant="outline" onClick={() => navigate('/sign-up')} className="border-white text-white hover:bg-white/10 rounded-xl" data-testid="signin-inline-cta">{t('jobs.signIn')}</Button>
            </div>
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            {t('footer.joinBoholanos')}
          </h3>
          <p className="text-gray-300 mb-6 text-lg">
            {t('footer.connectLocal')}
          </p>
          
          <div className="flex items-center justify-center gap-6 mb-8">
            <a
              href="https://www.facebook.com/profile.php?id=61584702491513"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-white transition-colors"
              aria-label="Facebook"
            >
              <Facebook size={28} />
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 mb-3">
            <Link to="/privacy" className="text-sm text-gray-400 hover:text-white hover:underline" data-testid="footer-privacy-link">
              {t('footer.privacy')}
            </Link>
            <span className="text-gray-500">·</span>
            <Link to="/terms" className="text-sm text-gray-400 hover:text-white hover:underline" data-testid="footer-terms-link">
              {t('footer.terms', 'Terms of Service')}
            </Link>
            <span className="text-gray-500">·</span>
            <Link to="/delete-data" className="text-sm text-gray-400 hover:text-white hover:underline" data-testid="footer-delete-link">
              {t('footer.deleteData')}
            </Link>
          </div>
          
          <p className="text-gray-400 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
            {t('footer.copyright')}
          </p>
          <p className="text-gray-500 text-xs mt-2" style={{ fontFamily: 'Inter, sans-serif' }} data-testid="app-version">
            {t('footer.version')} {appVersion}
          </p>
        </div>
      </footer>

      <BottomNav profile={profile} />
    </div>
  );
};

export default Jobs;
